import { usePageMeta } from "@/lib/seo";
import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";

const CONTACT_EMAIL = "hello@justbeyou.co.in";

const POLICIES = {
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: July 2026",
    sections: [
      ["About us", "justbeyou.co.in (\u201cJust be you\u201d, \u201cwe\u201d, \u201cour\u201d) is operated by Mehjabin, offering digital products (e-books, guided meditations), physical products (journals, card decks), and personal services (1:1 mentoring, coaching, tarot & oracle readings, and group workshops)."],
      ["Acceptance of terms", "By accessing this website, purchasing a product, or booking a session, you agree to these Terms & Conditions. If you do not agree, please do not use this website."],
      ["Products & services", "All product descriptions, session details, and prices are displayed on the relevant product or booking page. Prices are listed in Indian Rupees (INR) and, for reference, in US Dollars. All payments are processed in INR."],
      ["Payments", "Payments are processed securely through Razorpay. We do not store your card, UPI, or banking details. An order is confirmed only after successful payment."],
      ["Digital products", "Digital products are delivered to your email and account dashboard after purchase. They are licensed for your personal use only and may not be shared, resold, or redistributed."],
      ["Sessions & workshops", "Bookings are confirmed by email. Session timings are as selected during booking. Please see our Refund & Cancellation Policy for rescheduling and cancellation terms."],
      ["Not medical or financial advice", "Our content, sessions, and readings are for personal growth, reflection, and entertainment purposes. They are not a substitute for professional medical, psychological, legal, or financial advice."],
      ["Intellectual property", "All content on this website \u2014 text, imagery, e-books, guided meditations, and branding \u2014 is original work created, authored, and owned by Just be you \u00b7 by Mehjabin. No third-party or licensed media is resold on this website. All material is protected by applicable copyright laws."],
      ["Governing law", "These terms are governed by the laws of India. Any disputes are subject to the jurisdiction of Indian courts."],
      ["Contact", `For any questions about these terms, write to ${CONTACT_EMAIL}.`],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    sections: [
      ["What we collect", "When you purchase, book, or join our mailing list, we collect your name, email address, and \u2014 for physical orders \u2014 your shipping address and phone number. We never see or store your payment card, UPI, or banking details; these are handled entirely by Razorpay."],
      ["How we use your information", "We use your details to deliver purchases, confirm bookings, send order and booking emails, and \u2014 only if you have joined our list \u2014 send occasional letters. We do not sell or rent your personal information to anyone."],
      ["Emails", "Transactional emails (order confirmations, booking confirmations, digital downloads) are sent through Brevo. You can unsubscribe from non-essential letters at any time using the link in the email."],
      ["Payment processing", "Payments are processed by Razorpay Software Pvt. Ltd. Their handling of your payment data is governed by Razorpay's own privacy policy."],
      ["Cookies & analytics", "We use only the cookies necessary for the website to function (such as keeping you signed in and remembering your basket). We do not use third-party advertising trackers."],
      ["Data retention & deletion", `We keep order records as required for accounting. You may request access to, correction of, or deletion of your personal data at any time by writing to ${CONTACT_EMAIL}.`],
      ["Contact", `For any privacy questions, write to ${CONTACT_EMAIL}.`],
    ],
  },
  refunds: {
    title: "Refund & Cancellation Policy",
    updated: "Last updated: July 2026",
    sections: [
      ["Digital products", "Because digital products (e-books, meditations, downloads) are delivered instantly, they are non-refundable once delivered. If you receive a broken or incorrect file, write to us within 7 days and we will replace it or refund you in full."],
      ["Pre-orders", "Pre-ordered items can be cancelled for a full refund any time before the stated delivery date."],
      ["Physical products", "If your item arrives damaged or incorrect, contact us within 7 days of delivery with photos and we will send a replacement or issue a full refund. Please do not return items without writing to us first."],
      ["1:1 sessions (mentoring, coaching, readings)", "You may reschedule a session free of charge with at least 24 hours' notice. Cancellations made 48 hours or more before the session receive a full refund. Cancellations within 48 hours are non-refundable, but we will always try to find a kind solution."],
      ["Workshops", "Workshop seats are refundable up to 48 hours before the workshop begins. Within 48 hours, your seat may be transferred to a future workshop instead."],
      ["How refunds are processed", "Approved refunds are issued to your original payment method via Razorpay within 5\u20137 business days."],
      ["How to request", `Write to ${CONTACT_EMAIL} with your order ID and we will respond within 24\u201348 hours.`],
    ],
  },
  shipping: {
    title: "Shipping & Delivery Policy",
    updated: "Last updated: July 2026",
    sections: [
      ["Digital delivery", "E-books, meditations, and other digital products are delivered instantly after payment \u2014 by email and in your account dashboard. Pre-ordered digital products are delivered by the date stated on the product page."],
      ["Physical shipping \u2014 India", "Physical items (journals, card decks) are dispatched within 3 business days of your order and typically arrive within 7\u201314 days via trusted courier partners."],
      ["International shipping", "International orders typically arrive within 14\u201321 days. Any customs duties levied by your country are the recipient's responsibility."],
      ["Order tracking", "You will receive a dispatch confirmation email once your order ships."],
      ["Delays & issues", `If your order hasn't arrived within the expected window, write to ${CONTACT_EMAIL} with your order ID and we will chase it down for you.`],
    ],
  },
  contact: {
    title: "Contact Us",
    updated: "We respond within 24\u201348 hours",
    sections: [
      ["Business name", "Just be you \u00b7 by Mehjabin"],
      ["Email", CONTACT_EMAIL],
      ["Website", "justbeyou.co.in"],
      ["Location", "India"],
      ["Booking questions", "For anything about an upcoming session \u2014 rescheduling, preparation, or timing \u2014 reply to your booking confirmation email or write to the address above with your booking date."],
      ["Order questions", "For orders, include your order ID (from your confirmation email) so we can help you faster."],
    ],
  },
};

export default function PolicyPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug] || POLICIES.terms;
  usePageMeta(`${policy.title} | Just be you`);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={`policy-page-${slug}`}>
      <div className="max-w-3xl">
        <div className="overline mb-4">The fine print</div>
        <h1 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight" data-testid="policy-title">
          {policy.title}
        </h1>
        <p className="mt-3 text-sm text-ink/50">{policy.updated}</p>

        <div className="mt-12 space-y-10">
          {policy.sections.map(([heading, body]) => (
            <section key={heading}>
              <h2 className="font-serif text-lg text-ink font-medium mb-2">{heading}</h2>
              <p className="text-ink/70 leading-relaxed text-sm md:text-base">{body}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-lapis/10 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          {Object.entries(POLICIES).map(([key, p]) => (
            <Link
              key={key}
              to={`/policies/${key}`}
              className={`transition-colors ${key === slug ? "text-lapis font-semibold" : "text-ink/60 hover:text-lapis"}`}
              data-testid={`policy-link-${key}`}
            >
              {p.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
